import type { Conversation } from '../types/chat.types'
import type { IHttpClient } from './httpClient'

interface BackendConversation {
  conversationId?: string
  title?: string
  preview?: string
  updatedAt?: string
  createdAt?: string
}

interface ConversationsResponse {
  conversations?: BackendConversation[]
  pinnedConversations?: BackendConversation[]
}

export interface ConversationsData {
  conversations: Conversation[]
  pinnedConversations: Conversation[]
}

export interface IConversationService {
  getConversations(tenantId: string): Promise<ConversationsData>
  updateConversationTitle(tenantId: string, conversationId: string, title: string): Promise<void>
  pinConversation(tenantId: string, conversationId: string): Promise<void>
  unpinConversation(tenantId: string, conversationId: string): Promise<void>
  deleteConversation(tenantId: string, conversationId: string): Promise<void>
}

function mapConversation(raw: BackendConversation): Conversation | null {
  if (!raw.conversationId) return null
  return {
    id: raw.conversationId,
    title: raw.title || 'Untitled Conversation',
    preview: raw.preview || '',
    status: 'active',
    updatedAt: raw.updatedAt
      ? new Date(raw.updatedAt)
      : raw.createdAt
      ? new Date(raw.createdAt)
      : new Date(),
  }
}

export class ConversationService implements IConversationService {
  constructor(private httpClient: IHttpClient) {}

  async getConversations(tenantId: string): Promise<ConversationsData> {
    const response = await this.httpClient.get<ConversationsResponse>('/conversations', {
      params: { tenantId },
    })

    const conversations = (response.conversations || [])
      .map(mapConversation)
      .filter((conversation): conversation is Conversation => conversation !== null)
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())

    const pinnedConversations = (response.pinnedConversations || [])
      .map(mapConversation)
      .filter((conversation): conversation is Conversation => conversation !== null)
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())

    return {
      conversations,
      pinnedConversations,
    }
  }

  async updateConversationTitle(
    tenantId: string,
    conversationId: string,
    title: string
  ): Promise<void> {
    const trimmed = title.trim()
    if (!trimmed) {
      throw new Error('Conversation title cannot be empty')
    }

    await this.httpClient.post(
      '/update-conversation',
      {
        tenantId,
        conversationId,
        conversationName: trimmed,
      },
      { timeout: 15000 }
    )
  }

  async pinConversation(tenantId: string, conversationId: string): Promise<void> {
    await this.httpClient.post('/update-conversation', {
      tenantId,
      conversationId,
      pin: true,
    })
  }

  async unpinConversation(tenantId: string, conversationId: string): Promise<void> {
    await this.httpClient.post('/update-conversation', {
      tenantId,
      conversationId,
      pin: false,
    })
  }

  async deleteConversation(tenantId: string, conversationId: string): Promise<void> {
    await this.httpClient.delete('/conversations', {
      params: {
        tenantId,
        conversationId,
      },
    })
  }
}

